import { action, computed, makeObservable, observable, transaction } from "mobx"
import { RootStore } from "stores/root.store"
import { PaginationStore } from "stores/data/pagination.store"
import {
  createDailyMission,
  deleteDailyMission,
  editNativeGameTask,
  getAvailableNativeGames,
  getBonusSpendMissions,
  getDailyMissions,
  setBonusSpendMissions,
  updateDailyMission,
  updateNativeGame, deleteNativeGame,
  createNativeGame,
  restoreNativeGame,
  createNativeGameTask,
} from "../api/native-games-control.call"
import {
  BonusSpendMission,
  BonusSpendMissionTier,
  CreateDailyMissionRequest,
  CreateGameTask,
  CreateNativeGameRequest,
  DailyMission,
  GamePlatformType,
  NativeGame,
  NativeGameFilter,
  UpdateDailyMissionRequest,
  UpdateGameTask,
} from "./types"


export class NativeGamesControlStore extends PaginationStore<
  NativeGame,
  NativeGameFilter
> {
  @observable
  private _nativeGames: NativeGame[] = []
  @observable
  public loadedPages: number = 1
  @observable
  public isEmptyArr: boolean = false

  constructor(rootStore: RootStore) {
    super(rootStore)
    makeObservable(this)
  }

  // Sets the filter and resets infinite-scroll paging in one go.
  // Callers must use this (not the inherited setFilter) so that flipping
  // source/hideDeleted/name doesn't re-fetch page N of the new filter and
  // append it to the prior list, mixing sources together.
  // (Can't override PaginationStore.setFilter directly — it's declared as
  // an arrow-property instance field, and neither @action nor mobx
  // @override can re-decorate that shape.)
  @action
    applyFilter = (filter: NativeGameFilter) => {
      this.filter = filter
      this.pagination.page = 1
      this.loadedPages = 1
      this._nativeGames = []
    }

  public async getData(): Promise<NativeGame[]> {
    return this.getNativeGames()
  }

  public async getNativeGames() {
    this.resetFuncAfterSuccess()
    if (this._nativeGames.length === 0) {
      this.loadedPages = 1
    }
    this.isEmptyArr = false

    const nativeGames = await this.defaultFunction(
      getAvailableNativeGames,
      this.loadedPages,
      50,
      this.filter,
    )
    if (nativeGames) {
      transaction(() => {
        this.isLoading = false
        if (nativeGames.data?.length === 0) {
          this.isEmptyArr = true
          this.loadedPages = 0
        }
        if (this.loadedPages > 1) {
          this._nativeGames = [...this._nativeGames, ...nativeGames.data]
        } else {
          this._nativeGames = nativeGames.data
        }
        this.total = nativeGames.total
      })

      return nativeGames.data
    }
  }

  async editNativeGameTask(
    {
      gameId,
      taskId,
      ...data
    }: UpdateGameTask,
  ) {
    const response = await this.defaultFunction(editNativeGameTask, gameId, taskId, data)

    if (!response.success) {
      throw new Error(response?.data?.payload || "Unknown error occurred")
    }
  }

  async updateNativeGame(
    id: string,
    data: Partial<NativeGame>,
  ) {
    const response = await this.defaultFunction(updateNativeGame, id, data)
    if (response instanceof Error) {
      throw response
    }
  }

  async createNativeGame(data: CreateNativeGameRequest) {
    const response = await this.defaultFunction(createNativeGame, data)
    if (response instanceof Error) throw response
    await this.getNativeGames()
  }

  async deleteNativeGame(id: string) {
    await this.defaultFunction(deleteNativeGame, id)
  }

  async createNativeGameTask(gameId: string, data: CreateGameTask) {
    const response = await this.defaultFunction(createNativeGameTask, gameId, data)
    if (response instanceof Error) throw response

    return response
  }

  async setPlatformInstallableStatus(id: string, platform: GamePlatformType, isInstallable: boolean) {
    // defaultFunction catches and notifies on error but then returns the
    // error rather than re-throwing — the rest of the store follows this
    // pattern; we re-throw so callers don't show a success toast on top
    // of the already-shown error toast.
    const response = await this.defaultFunction(updateNativeGame, id, { platforms: [{ platform, isInstallable }] } as Partial<NativeGame>)
    if (response instanceof Error) throw response
  }

  async getBonusSpendMissions(gameId: string, platform: GamePlatformType): Promise<BonusSpendMission[]> {
    const response = await this.defaultFunction(getBonusSpendMissions, gameId, platform)
    if (response instanceof Error) throw response

    return response || []
  }

  async setBonusSpendMissions(
    gameId: string,
    platform: GamePlatformType,
    tiers: BonusSpendMissionTier[],
  ): Promise<BonusSpendMission[]> {
    const response = await this.defaultFunction(setBonusSpendMissions, gameId, platform, tiers)
    if (response instanceof Error) throw response

    return response || []
  }

  async getDailyMissions(gameId: string, platform: GamePlatformType): Promise<DailyMission[]> {
    const response = await this.defaultFunction(getDailyMissions, gameId, platform)
    if (response instanceof Error) throw response

    return response || []
  }

  async createDailyMission(data: CreateDailyMissionRequest): Promise<DailyMission> {
    const response = await this.defaultFunction(createDailyMission, data)
    if (response instanceof Error) throw response

    return response
  }

  async updateDailyMission(id: string, data: UpdateDailyMissionRequest): Promise<DailyMission> {
    const response = await this.defaultFunction(updateDailyMission, id, data)
    if (response instanceof Error) throw response

    return response
  }

  async deleteDailyMission(id: string): Promise<void> {
    const response = await this.defaultFunction(deleteDailyMission, id)
    if (response instanceof Error) throw response
  }

  async restoreNativeGame(id: string) {
    const response = await this.defaultFunction(restoreNativeGame, id)

    if (!response.success) {
      throw new Error(response?.data?.payload || "Unknown error occurred")
    }
  }

  @computed
  get nativeGames(): NativeGame[] {
    return this._nativeGames
  }

  set nativeGames(value: NativeGame[]) {
    this._nativeGames = value
  }
}
